import { Entypo, FontAwesome } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { Platform, Share, StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { Easing, FadeInRight, FadeOut } from 'react-native-reanimated';

import { ShowEpisode } from '@/api/models/showModels';
import { createShortDeepLink } from '@/utils/createDeepLink';
import { firebaseImage, safeJsonParseArray } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import Text from '../Text';

interface EpisodeSelectedHeaderParams {
  selectedEpisode: ShowEpisode;
  continueWatching?: boolean;
  numberOfEpisodes: number;
  onPlay: () => void;
}

const EpisodeSelectedHeader = ({
  selectedEpisode,
  continueWatching = false,
  numberOfEpisodes,
  onPlay
}: EpisodeSelectedHeaderParams) => {
  const handleShare = async () => {
    Share.share({
      message:
        (await createShortDeepLink(
          '/purchaseShow',
          firebaseImage(safeJsonParseArray(selectedEpisode?.thumbnail ?? '[]').at(0)) ?? undefined,
          i18n.t('trb_show.share_title'),
          i18n.t('trb_show.share_description')
        )) ?? ''
    }).catch((e) => {
      console.error(e);
    });
  };

  return (
    <Animated.View
      style={styles.container}
      entering={FadeInRight.duration(650).easing(Easing.out(Easing.cubic))}
      exiting={FadeOut.easing(Easing.ease)}>
      <View style={styles.titleContainer}>
        <View>
          <Text fontWeight="900" style={styles.title}>
            {selectedEpisode?.title}
          </Text>
          <Text style={styles.subtitle}>{i18n.t('trb_show.number_episodes', { LENGTH: numberOfEpisodes })}</Text>
        </View>
        <View style={styles.buttonsContainer}>
          {Platform.OS === 'web' && (
            <TouchableOpacity onPress={() => router.navigate('/show/giftSeries')}>
              <View style={styles.giftContainer}>
                <FontAwesome name="gift" size={24} color="#FE9722" />
                <Text style={styles.giftText}>{i18n.t('mix.gift')}</Text>
              </View>
            </TouchableOpacity>
          )}
          <TouchableOpacity onPress={handleShare}>
            <View style={styles.shareContainer}>
              <FontAwesome name="send" size={24} color="white" style={styles.shareIcon} />
              <Text style={styles.shareText}>{i18n.t('mix.share')}</Text>
            </View>
          </TouchableOpacity>
        </View>
      </View>
      <BrandedButton textStyles={styles.playTextStyles} style={styles.playButton} onPress={onPlay}>
        <View style={styles.playContent}>
          <Entypo name="controller-play" size={24} color="black" />
          <Text fontWeight="700" style={styles.playText}>
            {continueWatching ? i18n.t('trb_show.continue_watching') : i18n.t('trb_show.play')}
          </Text>
        </View>
      </BrandedButton>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    display: 'flex',
    gap: 15,
    zIndex: 99,
    overflow: 'hidden'
  },

  titleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%'
  },

  title: {
    color: 'white',
    fontSize: 18
  },

  subtitle: {
    color: 'white'
  },

  shareContainer: {
    gap: 2
  },

  buttonsContainer: {
    flexDirection: 'row',
    gap: 10
  },

  shareIcon: {
    transform: [{ rotate: '20deg' }]
  },

  shareText: {
    color: 'white',
    fontSize: 12
  },

  playTextStyles: {
    color: 'black'
  },

  playButton: {
    backgroundColor: 'white',
    width: '100%'
  },

  playContent: {
    flexDirection: 'row',
    gap: 2.5,
    alignItems: 'center',
    justifyContent: 'center'
  },

  playText: {
    color: 'black'
  },

  giftContainer: {
    gap: 2,
    justifyContent: 'center',
    alignItems: 'center'
  },

  giftText: {
    color: '#FE9722',
    fontSize: 12
  }
});

export default React.memo(EpisodeSelectedHeader);
